import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { isDependencyConflict, toDependentCounts } from './deleteConflict';
import type { useDeleteConfirm } from './useDeleteConfirm';

type DeleteConfirmDialogProps = {
    confirm: ReturnType<typeof useDeleteConfirm>;
    /**
     * i18n key for the prompt, interpolated with `{{name}}`. Each page supplies
     * its own so the question names the thing ("Delete the supplier Globex?")
     * rather than asking about "the item".
     */
    promptKey: string;
    /** i18n key for the line under the prompt. */
    descriptionKey?: string;
};

/**
 * Confirmation for a delete the server may refuse.
 *
 * Render-only. The refusal is shown **inline, in the dialog the user is already
 * looking at**, rather than as a toast: the server's message is a sentence with
 * a remedy in it, and a notification that leaves after four seconds is not
 * somewhere a remedy can be read. Where the entity supports archiving, the
 * secondary action appears only once the refusal names dependants.
 */
export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
    confirm,
    promptKey,
    descriptionKey,
}) => {
    const { t } = useTranslation('components');
    const { target, error, isPending, canArchive } = confirm;

    const blockers = toDependentCounts(error?.dependents);
    const isConflict = isDependencyConflict(error);

    return (
        <Modal
            isOpen={target !== null}
            onOpenChange={(open) => !open && confirm.onCancel()}
        >
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp('delete-confirm-dialog')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading
                                {...getNodeTestIdProp('delete-confirm-title')}
                            >
                                {t('deleteConfirm.title')}
                            </Modal.Heading>
                            <p
                                className="text-sm text-muted"
                                {...getNodeTestIdProp('delete-confirm-prompt')}
                            >
                                {t(promptKey, { name: target?.name ?? '' })}
                            </p>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-4 pb-2">
                            {descriptionKey && !isConflict ? (
                                <p className="text-sm text-muted">
                                    {t(descriptionKey)}
                                </p>
                            ) : null}

                            {error ? (
                                <div
                                    className="flex flex-col gap-3 rounded-xl bg-danger-soft p-4"
                                    {...getNodeTestIdProp(
                                        'delete-confirm-error',
                                    )}
                                >
                                    <div className="flex items-start gap-2">
                                        <Icon
                                            aria-hidden
                                            className="mt-0.5 size-4 shrink-0 text-danger-soft-foreground"
                                            icon="gravity-ui:circle-exclamation"
                                        />
                                        <p
                                            className="text-sm text-danger-soft-foreground"
                                            {...getNodeTestIdProp(
                                                'delete-confirm-error-message',
                                            )}
                                        >
                                            {error.message}
                                        </p>
                                    </div>
                                    {blockers.length > 0 ? (
                                        <ul
                                            className="flex flex-col gap-1 pl-6 text-sm text-danger-soft-foreground"
                                            {...getNodeTestIdProp(
                                                'delete-confirm-dependents',
                                            )}
                                        >
                                            {blockers.map((blocker) => (
                                                <li
                                                    className="tabular-nums"
                                                    key={blocker.kind}
                                                >
                                                    {`${blocker.count} · ${t(
                                                        blocker.labelKey,
                                                        {
                                                            defaultValue:
                                                                blocker.kind,
                                                        },
                                                    )}`}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                </div>
                            ) : null}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onPress={confirm.onCancel}
                                variant="tertiary"
                                {...getNodeTestIdProp('delete-confirm-cancel')}
                            >
                                {t(
                                    isConflict
                                        ? 'deleteConfirm.close'
                                        : 'deleteConfirm.cancel',
                                )}
                            </Button>
                            {canArchive ? (
                                <Button
                                    isDisabled={isPending}
                                    onPress={confirm.onArchiveInstead}
                                    variant="secondary"
                                    {...getNodeTestIdProp(
                                        'delete-confirm-archive',
                                    )}
                                >
                                    {t('deleteConfirm.archiveInstead')}
                                </Button>
                            ) : null}
                            {isConflict ? null : (
                                <Button
                                    isDisabled={isPending}
                                    onPress={confirm.onConfirm}
                                    variant="danger"
                                    {...getNodeTestIdProp(
                                        'delete-confirm-submit',
                                    )}
                                >
                                    {t('deleteConfirm.submit')}
                                </Button>
                            )}
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
